import { IMAGE_MIME_TYPES } from 'pages/Batch/configs';
import type { IWatermarkAttrs } from 'pages/Batch/types';
import { DEFAULT_WATERMARK_ATTRS } from 'pages/Batch/sections/Sidebar/WatermarkTool/constants';

import isEmpty from 'lodash/isEmpty';
import range from 'lodash/fp/range';
import type { IProcessResult } from '../process/toolchain';
import { WatermarksPositions } from './configs';
import { clearLogoCache } from './logoCache';

const MARGIN_PERCENTAGE = 0.06;

class WatermarkGenerator {
  logo: HTMLImageElement | undefined;

  attrs: IWatermarkAttrs;

  isPreviewMode: boolean;

  constructor() {
    this.attrs = DEFAULT_WATERMARK_ATTRS;
    this.isPreviewMode = true;
  }

  private calculateLogoSize(originalImgCanvas: HTMLCanvasElement, attrs: IWatermarkAttrs, logo: HTMLImageElement) {
    const logoSize = attrs.size / 100;
    const desiredLogoWidth = originalImgCanvas.width * logoSize;
    const desiredLogoHeight = originalImgCanvas.height * logoSize;
    const scaleX = desiredLogoWidth / logo.width;
    const scaleY = desiredLogoHeight / logo.height;
    const scaleFactor = Math.min(scaleX, scaleY);

    const logoWidth = logo.width * scaleFactor;
    const logoHeight = logo.height * scaleFactor;

    return { logoWidth, logoHeight };
  }

  private calculateLogoPosition(ctx: CanvasRenderingContext2D, logoWidth: number, logoHeight: number, attrs: IWatermarkAttrs) {
    const { position } = attrs;
    const imageWidth = ctx.canvas.width;
    const imageHeight = ctx.canvas.height;
    const margin = MARGIN_PERCENTAGE * Math.min(imageWidth, imageHeight);

    let xPos = 0;
    let yPos = 0;

    switch (position) {
      case WatermarksPositions.RIGHT_TOP:
        xPos = imageWidth - logoWidth - margin;
        yPos = margin;
        break;
      case WatermarksPositions.RIGHT_BOTTOM:
        xPos = imageWidth - logoWidth - margin;
        yPos = imageHeight - logoHeight - margin;
        break;
      case WatermarksPositions.LEFT_TOP:
        xPos = margin;
        yPos = margin;
        break;
      case WatermarksPositions.LEFT_BOTTOM:
        xPos = margin;
        yPos = imageHeight - logoHeight - margin;
        break;
      default:
        break;
    }

    return { xPos, yPos };
  }

  private drawOriginalImage(image: HTMLImageElement, isPreviewMode: boolean) {
    // In preview mode, a resized canvas is used to optimize performance, since a full-sized canvas is needed only for Apply mode
    return isPreviewMode ? this.createResizedCanvas(image) : this.createOriginalCanvas(image);
  }

  private drawLogo(ctx: CanvasRenderingContext2D, xPos: number, yPos: number, logoWidth: number, logoHeight: number, attrs: IWatermarkAttrs, logo: HTMLImageElement) {
    const { position, opacity, rotateAngle } = attrs;
    const logoImg = logo;

    // Set the global alpha (transparency) for the logo based on the specified opacity
    ctx.globalAlpha = opacity / 100;

    if (position === WatermarksPositions.RIGHT_FULL || position === WatermarksPositions.LEFT_FULL) {
      // Create a grid of logos
      const logosGridCanvas = this.createLogosGrid(ctx, logoWidth, logoHeight, logo);
      const logosGridCanvasWidth = logosGridCanvas.width;
      const logosGridCanvasHeight = logosGridCanvas.height;

      // Rotate the grid of logos
      const rotatedLogosGridCanvas = this.rotateCanvas(logosGridCanvas, rotateAngle);

      // Apply(Draw) grid of logos on the original image
      ctx.drawImage(rotatedLogosGridCanvas, -logosGridCanvasWidth / 4, -logosGridCanvasHeight / 4, logosGridCanvasWidth, logosGridCanvasHeight);
    } else {
      this.drawRotatedLogo(logoImg, rotateAngle, ctx, xPos, yPos, logoWidth, logoHeight);
    }
  }

  private drawRotatedLogo(logoImg: HTMLImageElement, angle: number, ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number) {
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    const angleInRadians = (angle * Math.PI) / 180;

    ctx.save();
    ctx.translate(centerX, centerY);

    // Rotate only if the angle is not 0
    if (angle !== 0) {
      ctx.rotate(angleInRadians);
    }

    ctx.drawImage(logoImg, -width / 2, -height / 2, width, height);
    ctx.restore();
  }

  private rotateCanvas(canvas: HTMLCanvasElement, angle: number): HTMLCanvasElement {
    const rotatedCanvas = document.createElement('canvas');
    const rotatedCtx = rotatedCanvas.getContext('2d')!;

    // Set the new canvas size based on the rotated dimensions
    rotatedCanvas.width = canvas.width;
    rotatedCanvas.height = canvas.height;

    // Translate to the center of the canvas and rotate
    rotatedCtx.translate(canvas.width / 2, canvas.height / 2);
    rotatedCtx.rotate((angle * Math.PI) / 180);
    rotatedCtx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);

    return rotatedCanvas;
  }

  private createResizedCanvas(image: HTMLImageElement) {
    const canvas = document.createElement('canvas');
    const canvasMaxSize = 700;
    const { width, height } = image;

    const imgBigSide = Math.max(width, height);
    const imgScaleFactor = imgBigSide / canvasMaxSize;
    const desiredWidth = width / imgScaleFactor;
    const desiredHeight = height / imgScaleFactor;

    canvas.width = desiredWidth;
    canvas.height = desiredHeight;

    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(image, 0, 0, desiredWidth, desiredHeight);

    return ctx;
  }

  private createOriginalCanvas(image: HTMLImageElement) {
    // Fresh canvas per call: Apply renders working-set items in parallel, so a shared instance canvas
    // would let concurrent draws clobber each other before toBlob captures the result.
    const canvas = document.createElement('canvas');
    const { width, height } = image;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(image, 0, 0);

    return ctx;
  }

  private createLogosGrid(ctx: CanvasRenderingContext2D, logoWidth: number, logoHeight: number, logo: HTMLImageElement): HTMLCanvasElement {
    const logoImg = logo;
    const originalImgHeight = ctx.canvas.height;
    const originalImgWidth = ctx.canvas.width;

    // Create a container square canvas for the grid of logos to be filled inside
    const logosGridCanvas = document.createElement('canvas');
    const logosGridCanvasCtx = logosGridCanvas.getContext('2d')!;
    const squareGridSize = Math.max(originalImgWidth, originalImgHeight) * 1.8;
    logosGridCanvas.width = squareGridSize;
    logosGridCanvas.height = squareGridSize;

    // Arrange logos with equal spacing on the container canvas.
    const minSpacing = 10;
    const cols = Math.floor(squareGridSize / (logoWidth + minSpacing * 2));
    const rows = Math.floor(squareGridSize / (logoHeight + minSpacing * 2));
    const widthSpacing = (squareGridSize - cols * logoWidth) / (cols + 1);
    const heightSpacing = (squareGridSize - rows * logoHeight) / (rows + 1);

    // Draw a grid(set) of logos
    range(0, cols).forEach(i => {
      range(0, rows).forEach(j => {
        const x = widthSpacing + i * (logoWidth + widthSpacing);
        const y = heightSpacing + j * (logoHeight + heightSpacing);
        logosGridCanvasCtx.drawImage(logoImg, x, y, logoWidth, logoHeight);
      });
    });

    return logosGridCanvas;
  }

  private setAttributes = (args?: IWatermarkAttrs) => {
    // TODO remove from args
    this.isPreviewMode = !isEmpty(args);

    if (this.isPreviewMode) {
      this.attrs = { ...DEFAULT_WATERMARK_ATTRS, ...args };
    }
  };

  init(logo: HTMLImageElement) {
    this.logo = logo;
  }

  clear() {
    this.logo = undefined;
    this.attrs = DEFAULT_WATERMARK_ATTRS;
    this.isPreviewMode = true;
    clearLogoCache();
  }

  // Compose the watermark from explicit attrs/logo (no instance state), so per-item Apply can render each
  // image with its own settings concurrently. isPreviewMode picks the downscaled (preview) vs full-res (apply) canvas.
  private compose(image: HTMLImageElement, attrs: IWatermarkAttrs, logo: HTMLImageElement | undefined, isPreviewMode: boolean): Promise<IProcessResult> {
    const ctx = this.drawOriginalImage(image, isPreviewMode);
    const originalImgCanvas = ctx.canvas;

    // Only overlay when a logo has been initialized — otherwise return the untouched image.
    // Prevents stale-logo bleed-through after a tool cancel and an undefined-logo crash when
    // the preview runs before any element is picked.
    if (logo && attrs.position) {
      const { logoWidth, logoHeight } = this.calculateLogoSize(originalImgCanvas, attrs, logo);
      const { xPos, yPos } = this.calculateLogoPosition(ctx, logoWidth, logoHeight, attrs);

      this.drawLogo(ctx, xPos, yPos, logoWidth, logoHeight, attrs, logo);
    }

    return new Promise(resolve => {
      if (isPreviewMode) {
        resolve({ result: ctx.canvas });
      } else {
        ctx.canvas.toBlob(blob => blob && resolve({ blob, result: URL.createObjectURL(blob) }), IMAGE_MIME_TYPES.WEBP);
      }
    });
  }

  generate =
    (attrs?: IWatermarkAttrs, logoOverride?: HTMLImageElement, isApply = false) =>
    async (image: HTMLImageElement): Promise<IProcessResult> => {
      // Per-item Apply: render full-res from the given attrs + logo without touching instance state, so
      // each working-set item bakes its own previewed watermark (see watermarkItemSettings). A missing
      // logo composes the untouched full-res image (compose no-ops the overlay).
      if (isApply) {
        return this.compose(image, { ...DEFAULT_WATERMARK_ATTRS, ...attrs }, logoOverride, false);
      }
      // Preview (non-empty attrs → downscaled) / legacy full-res apply (empty attrs) using instance state.
      this.setAttributes(attrs);
      return this.compose(image, this.attrs, this.logo, this.isPreviewMode);
    };
}

export default new WatermarkGenerator();
